/**
 * SetTypeBadge — reusable compact badge for workout set types.
 *
 * Covers all 12 set types from the Forge spec:
 *   straight, overload, hypertrophy, drop_set, superset, giant,
 *   rest_pause, mechanical_drop, cluster, amrap, feeder, back_off
 *
 * Legacy aliases still supported: warmup → feeder, kill → overload
 */
import React from 'react';
import {
  Flame, Skull, TrendingDown, AlertTriangle, Target,
  Zap, BarChart2, Link, Layers, RefreshCw, ChevronDown, ArrowDownCircle,
} from 'lucide-react';

export type SetTypeValue =
  | 'normal'
  | 'straight'
  | 'overload'
  | 'hypertrophy'
  | 'warmup'       // legacy alias → feeder
  | 'kill'         // legacy alias → overload
  | 'drop_set'
  | 'superset'
  | 'giant'
  | 'rest_pause'
  | 'mechanical_drop'
  | 'cluster'
  | 'failure'      // legacy alias → amrap
  | 'amrap'
  | 'feeder'
  | 'back_off'
  | null
  | undefined;

export interface SetTypeInfo {
  label: string;
  shortLabel: string;
  description: string;
  icon: React.ReactNode;
  colorClass: string;
  bgClass: string;
  borderClass: string;
}

export const ALL_SET_TYPES: { value: SetTypeValue; info: SetTypeInfo }[] = [
  {
    value: 'straight',
    info: {
      label: 'Straight Set',
      shortLabel: 'STRAIGHT',
      description: 'Standard working set — same weight, same reps each set',
      icon: <Layers className="h-3 w-3" />,
      colorClass: 'text-slate-300',
      bgClass: 'bg-slate-500/10',
      borderClass: 'border-slate-500/30',
    },
  },
  {
    value: 'overload',
    info: {
      label: 'Overload Set',
      shortLabel: 'OVERLOAD',
      description: 'Max effort progressive set — weight is the target',
      icon: <Zap className="h-3 w-3" />,
      colorClass: 'text-blue-400',
      bgClass: 'bg-blue-500/10',
      borderClass: 'border-blue-500/30',
    },
  },
  {
    value: 'hypertrophy',
    info: {
      label: 'Hypertrophy Set',
      shortLabel: 'HYPER',
      description: 'Rep range focus — volume accumulation before weight increase',
      icon: <BarChart2 className="h-3 w-3" />,
      colorClass: 'text-emerald-400',
      bgClass: 'bg-emerald-500/10',
      borderClass: 'border-emerald-500/30',
    },
  },
  {
    value: 'drop_set',
    info: {
      label: 'Drop Set',
      shortLabel: 'DROP',
      description: 'Start heavy, immediately reduce weight and continue reps without rest',
      icon: <TrendingDown className="h-3 w-3" />,
      colorClass: 'text-orange-400',
      bgClass: 'bg-orange-500/10',
      borderClass: 'border-orange-500/30',
    },
  },
  {
    value: 'superset',
    info: {
      label: 'Superset',
      shortLabel: 'SUPER',
      description: 'Two exercises performed back to back with no rest between them',
      icon: <Link className="h-3 w-3" />,
      colorClass: 'text-orange-400',
      bgClass: 'bg-orange-500/10',
      borderClass: 'border-orange-500/30',
    },
  },
  {
    value: 'giant',
    info: {
      label: 'Giant Set',
      shortLabel: 'GIANT',
      description: 'Three or more exercises performed in sequence with no rest',
      icon: <Layers className="h-3 w-3" />,
      colorClass: 'text-orange-500',
      bgClass: 'bg-orange-600/10',
      borderClass: 'border-orange-600/30',
    },
  },
  {
    value: 'rest_pause',
    info: {
      label: 'Rest-Pause',
      shortLabel: 'R-PAUSE',
      description: 'Reps to near-failure, rest 10-15 sec, continue same weight',
      icon: <RefreshCw className="h-3 w-3" />,
      colorClass: 'text-purple-400',
      bgClass: 'bg-purple-500/10',
      borderClass: 'border-purple-500/30',
    },
  },
  {
    value: 'mechanical_drop',
    info: {
      label: 'Mechanical Drop',
      shortLabel: 'MECH',
      description: 'Same weight, transition to easier variation when fatigued',
      icon: <ChevronDown className="h-3 w-3" />,
      colorClass: 'text-purple-400',
      bgClass: 'bg-purple-500/10',
      borderClass: 'border-purple-500/30',
    },
  },
  {
    value: 'cluster',
    info: {
      label: 'Cluster Set',
      shortLabel: 'CLUSTER',
      description: 'Heavy weight with short intra-set rests (10-15 sec) between mini-sets',
      icon: <Layers className="h-3 w-3" />,
      colorClass: 'text-purple-400',
      bgClass: 'bg-purple-500/10',
      borderClass: 'border-purple-500/30',
    },
  },
  {
    value: 'amrap',
    info: {
      label: 'AMRAP',
      shortLabel: 'AMRAP',
      description: 'As Many Reps As Possible — max effort, log actual reps achieved',
      icon: <Target className="h-3 w-3" />,
      colorClass: 'text-red-400',
      bgClass: 'bg-red-500/10',
      borderClass: 'border-red-500/30',
    },
  },
  {
    value: 'feeder',
    info: {
      label: 'Feeder Set',
      shortLabel: 'FEEDER',
      description: 'Light warm-up set to prime the movement — not counted in volume',
      icon: <Flame className="h-3 w-3" />,
      colorClass: 'text-slate-400',
      bgClass: 'bg-slate-500/10',
      borderClass: 'border-slate-500/30',
    },
  },
  {
    value: 'back_off',
    info: {
      label: 'Back-Off Set',
      shortLabel: 'BACK-OFF',
      description: 'After heavy work, drop to 60-70% of working weight for higher reps',
      icon: <ArrowDownCircle className="h-3 w-3" />,
      colorClass: 'text-teal-400',
      bgClass: 'bg-teal-500/10',
      borderClass: 'border-teal-500/30',
    },
  },
];

export function getSetTypeBadgeConfig(
  setType: SetTypeValue,
  isWarmup?: boolean
): SetTypeInfo | null {
  // Resolve legacy aliases
  const resolved: SetTypeValue = isWarmup
    ? 'feeder'
    : setType === 'kill'
    ? 'overload'
    : setType === 'warmup'
    ? 'feeder'
    : setType === 'failure'
    ? 'amrap'
    : setType;

  const entry = ALL_SET_TYPES.find((t) => t.value === resolved);
  return entry?.info ?? null;
}

interface SetTypeBadgeProps {
  setType?: SetTypeValue;
  isWarmup?: boolean;
  /** Compact mode: icon only, no label text */
  compact?: boolean;
  className?: string;
}

export default function SetTypeBadge({
  setType,
  isWarmup,
  compact = false,
  className = '',
}: SetTypeBadgeProps) {
  const config = getSetTypeBadgeConfig(setType, isWarmup);
  if (!config) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-mono text-[10px] font-bold
        ${config.colorClass} ${config.bgClass} ${config.borderClass} ${className}`}
    >
      {config.icon}
      {!compact && <span>{config.shortLabel}</span>}
    </span>
  );
}
